import React, { useEffect, useState } from "react";
import GenericTable from "../../../components/shared/RefundableItemTable.jsx";
import GenericButton from "../../../components/shared/GenericButton.jsx";
import OrderLevelRefund from "./OrderLevelRefund.jsx";
import { connect } from "react-redux";
import { formatNumber, isExactMatch } from "../../../shared/utility.js";
import { refundOrderActions } from "../../../redux/actions/refundOrder.action.js";
import { MESSAGES } from "../../../constants/global.constants.js";

const TAB_KEYS = {
  INDIVIDUAL_ITEMS: 1,
  ENTIRE_ORDER: 2,
  ORDER_LEVEL_REFUND: 3,
};

const tabList = [
  { id: TAB_KEYS.INDIVIDUAL_ITEMS, label: "Refund individual items" },
  { id: TAB_KEYS.ENTIRE_ORDER, label: "Refund entire order" },
  { id: TAB_KEYS.ORDER_LEVEL_REFUND, label: "Apply an order level refund" },
];

const CustomTabs = (props) => {
  const { order, products, orderId, currencyCode } = props;

  const [rows, setRows] = useState([]);
  const [activeTab, setActiveTab] = useState(TAB_KEYS.INDIVIDUAL_ITEMS);

  const columns = [
    { label: "Item", accessor: "name", width: "40%" },
    { label: "SKU", accessor: "sku" },
    { label: "Quantity ordered", accessor: "quantity" },
    {
      label: "Unit price",
      accessor: "price_inc_tax",
      render: (row) => (
        <span className="text-content">{`${currencyCode}${formatNumber(
          row.price_inc_tax
        )}`}</span>
      ),
    },
    {
      label: "Units to refund",
      width: "10%",
      render: (row, disabledInputField) => {
        if (disabledInputField) {
          // When disabled, show a select with only one option (the quantity)
          return (
            <select
              className="form-select form-select-sm"
              value={row.inputValue || row.quantity}
              disabled
            >
              <option value={row.quantity}>{row.quantity}</option>
            </select>
          );
        }
        const isDisabled = row.refundableQuantity === 0;
        // When enabled, show full range dropdown as before
        return (
          <select
            className="form-select form-select-sm"
            value={row.inputValue || 0}
            onChange={(e) => handleRowInputChange(row.id, e.target.value)}
            disabled={isDisabled}
          >
            {Array.from({ length: row.refundableQuantity + 1 }, (_, i) => (
              <option key={i} value={i}>
                {i}
              </option>
            ))}
          </select>
        );
      },
    },
    {
      label: "Refund item total",
      width: "10%",
      render: (row) => (
        <span>
          {row.refundTotal
            ? `${currencyCode}${row.refundTotal}`
            : `${currencyCode}0.00`}
        </span>
      ),
    },
  ];

  useEffect(() => {
    setRows(
      products.map((item) => {
        const refundableQuantity =
          item.quantity - (item.quantity_refunded || 0);
        return {
          ...item,
          inputValue: 0,
          refundTotal: "0.00",
          refundableQuantity: Math.max(refundableQuantity, 0), // Avoid negative
        };
      })
    );
  }, [products]);

  useEffect(() => {
    if (activeTab === TAB_KEYS.ENTIRE_ORDER) {
      setRows((prevRows) =>
        prevRows.map((row) => ({
          ...row,
          inputValue: row.refundableQuantity,
          refundTotal: formatNumber(row.refundableQuantity * row.price_inc_tax),
        }))
      );
    } else {
      setRows((prevRows) =>
        prevRows.map((row) => ({
          ...row,
          inputValue: 0,
          refundTotal: "0.00",
        }))
      );
    }
  }, [activeTab]);

  const handleRowInputChange = (rowId, value) => {
    const numValue = +value;
    setRows((prev) =>
      prev.map((row) =>
        row.id === rowId
          ? {
              ...row,
              inputValue: numValue,
              refundTotal: formatNumber(numValue * row.price_inc_tax),
            }
          : row
      )
    );
  };

  const handleRefundClick = (value) => {
    if (activeTab === TAB_KEYS.INDIVIDUAL_ITEMS) {
      const refundSkus = rows
        .filter((row) => row.inputValue > 0)
        .map((row) => ({
          id: row.id,
          quantity: row.inputValue,
        }));

      const dataToSend = {
        refund_type: 2,
        skus: refundSkus,
      };

      props.addRefundOrder(dataToSend, orderId);
    } else if (activeTab === TAB_KEYS.ENTIRE_ORDER) {
      const dataToSend = {
        refund_type: 1,
      };
      props.addRefundOrder(dataToSend, orderId);
    } else if (activeTab === TAB_KEYS.ORDER_LEVEL_REFUND) {
      const dataToSend = {
        refund_type: 3,
        amount: value,
      };

      props.addRefundOrder(dataToSend, orderId);
    }
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case TAB_KEYS.INDIVIDUAL_ITEMS:
        return (
          <div className="tab-pane active">
            <GenericTable
              columns={columns}
              rows={rows}
              order={order}
              currencyCode={currencyCode}
            />
            {!isExactMatch(order.status, MESSAGES.REFUNDED) && (
              <div className="d-flex justify-content-end mb-3">
                <GenericButton
                  btnText={"Refund"}
                  handleClick={handleRefundClick}
                  loading={props.buttonSpinnerActivate}
                />
              </div>
            )}
          </div>
        );
      case TAB_KEYS.ENTIRE_ORDER:
        return (
          <div className="tab-pane active">
            <GenericTable
              columns={columns}
              rows={rows}
              disabledInputField={true}
              order={order}
              tableDisabled={true}
              currencyCode={currencyCode}
            />
            <div className="d-flex justify-content-end mb-3">
              {!isExactMatch(order.status, MESSAGES.REFUNDED) && (
                <GenericButton
                  btnText={"Refund"}
                  handleClick={handleRefundClick}
                  loading={props.buttonSpinnerActivate}
                />
              )}
            </div>
          </div>
        );
      case TAB_KEYS.ORDER_LEVEL_REFUND:
        return (
          <div className="tab-pane active">
            <OrderLevelRefund
              order={order}
              currencyCode={currencyCode}
              handleClick={handleRefundClick}
              loading={props.buttonSpinnerActivate}
            />
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="tabs-component mt-4">
      <ul className="nav nav-tabs">
        {tabList.map((tab) => (
          <li className="nav-item" key={tab.id}>
            <button
              className={`nav-link ${activeTab === tab.id ? "active" : ""}`}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
            </button>
          </li>
        ))}
      </ul>

      <div className="tab-content pt-4">{renderTabContent()}</div>
    </div>
  );
};

// Mapping the component's props to the reducer's state
const mapStateToProps = (state) => ({
  order: state.RefundOrder.orderData,
  products: state.RefundOrder.productsData,
  currencyCode: state.RefundOrder.currencyCode,
  buttonSpinnerActivate: state.System.buttonSpinnerActivate,
});

// Mapping the component's props to the related actions
const mapDispatchToProps = (dispatch) => ({
  addRefundOrder: (data, recordId) =>
    dispatch(refundOrderActions.addRefundOrder(data, recordId)),
});
export default connect(mapStateToProps, mapDispatchToProps)(CustomTabs);
